import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTrips } from '@/hooks/useTrips';
import { MapPin, Calendar, Heart, Trash2, Plus, Compass } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const TripList = ({ userId }) => {
  const navigate = useNavigate();
  const { tripsQuery, deleteMutation, updateMutation } = useTrips(userId);

  const trips = tripsQuery.data || [];

  const handleDelete = async (id) => {
    if (!confirm('确定要删除这个行程吗?')) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('已删除行程');
    } catch (err) {
      toast.error('删除失败');
    }
  };

  const handleToggleFavorite = async (trip) => {
    try {
      await updateMutation.mutateAsync({
        id: trip.id,
        is_favorite: !trip.is_favorite,
      });
      toast.success(trip.is_favorite ? '已取消收藏' : '已收藏');
    } catch (err) {
      toast.error('操作失败');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-text-primary">我的行程</h2>
        <Button onClick={() => navigate('/planner')} className="bg-primary hover:bg-primary-hover text-white rounded-btn px-5">
          <Plus className="w-4 h-4 mr-2" />
          新建行程
        </Button>
      </div>

      {trips.length === 0 ? (
        <Card className="border-0 shadow-card rounded-card bg-white/80 backdrop-blur">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-primary-light flex items-center justify-center mb-4">
              <Compass className="w-10 h-10 text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-text-primary mb-2">还没有行程</h3>
            <p className="text-text-secondary mb-6">开始规划你的第一次旅行吧</p>
            <Button onClick={() => navigate('/planner')} className="bg-primary hover:bg-primary-hover text-white rounded-btn px-6">
              <Plus className="w-4 h-4 mr-2" /> 创建行程
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {trips.map((trip) => (
            <Card
              key={trip.id}
              className="group border-0 shadow-card hover:shadow-hover transition-all cursor-pointer bg-white rounded-card overflow-hidden"
              onClick={() => navigate(`/trip/${trip.id}`)}
            >
              <div className="h-2 bg-gradient-to-r from-primary to-accent" />
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors line-clamp-1">
                    {trip.title || trip.destination}
                  </h3>
                  <button onClick={(e) => { e.stopPropagation(); handleToggleFavorite(trip); }} className="shrink-0">
                    <Heart className={`w-5 h-5 transition-colors ${trip.is_favorite ? 'fill-danger text-danger' : 'text-text-tertiary hover:text-danger'}`} />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-sm text-text-secondary mb-3">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span>{trip.destination}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-text-tertiary mb-4">
                  <Calendar className="w-3 h-3" />
                  <span>{format(new Date(trip.created_at), 'yyyy/MM/dd')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <Badge variant="secondary" className="bg-primary-light text-primary rounded-full text-xs">
                      {trip.days}天
                    </Badge>
                    {trip.style && (
                      <Badge variant="secondary" className="bg-accent/20 text-secondary rounded-full text-xs">
                        {trip.style}
                      </Badge>
                    )}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(trip.id); }} className="text-text-tertiary hover:text-danger transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
